import {graphql} from 'react-relay';
import type {Environment, GraphQLTaggedNode, MutationParameters, PayloadError} from 'relay-runtime';
import Util, {getValidationErrorMessageId} from '../../../shared/util/util';
import {trackAndCommitMutation} from '../../../../tracking/amplitude/TrackMutation';
import type {UploadTicket} from './uploadClientAssetFile';

const requestUploadMutation = graphql`
	mutation clientAssetMutationsRequestUploadMutation($input: RequestClientAssetUploadInput!) {
		requestClientAssetUpload(input: $input) {
			ticket {
				uploadUrl
				storageKey
				contentType
				expiresAt
			}
		}
	}
`;

const createMutation = graphql`
	mutation clientAssetMutationsCreateMutation($input: CreateClientAssetInput!) {
		createClientAsset(input: $input) {
			asset {
				id
				status
			}
		}
	}
`;

const uploadVersionMutation = graphql`
	mutation clientAssetMutationsUploadVersionMutation($input: UploadClientAssetVersionInput!) {
		uploadClientAssetVersion(input: $input) {
			asset {
				id
				status
			}
		}
	}
`;

const acknowledgeMutation = graphql`
	mutation clientAssetMutationsAcknowledgeMutation($input: AcknowledgeClientAssetInput!) {
		acknowledgeClientAsset(input: $input) {
			asset {
				id
				status
			}
		}
	}
`;

const archiveMutation = graphql`
	mutation clientAssetMutationsArchiveMutation($input: ArchiveClientAssetInput!) {
		archiveClientAsset(input: $input) {
			asset {
				id
				status
				archived
			}
		}
	}
`;

/** The `{commit}` class shape Util.CommitMutation drives, as in sendPhaseSignoffRequest.ts. */
const mutationClass = (mutation: GraphQLTaggedNode) => ({
	commit(
		environment: Environment,
		input: Record<string, unknown>,
		onSuccess: (response: unknown, errors: readonly PayloadError[] | null | undefined) => void,
		onError: (error: Error) => void
	) {
		return trackAndCommitMutation<MutationParameters>(environment, {
			mutation,
			variables: {
				input: {
					csrfToken: Util.getCsrfValue(),
					socketClientId: Util.getClientId(),
					...input,
				},
			},
			onCompleted: onSuccess,
			onError,
		} as never);
	},
});

export const RequestUploadMutation = mutationClass(requestUploadMutation);
export const CreateMutation = mutationClass(createMutation);
export const UploadVersionMutation = mutationClass(uploadVersionMutation);
export const AcknowledgeMutation = mutationClass(acknowledgeMutation);
export const ArchiveMutation = mutationClass(archiveMutation);

/**
 * Promise wrapper over Util.CommitMutation (never react-relay's commitMutation). A recognised
 * workspace refusal rejects with Error(messageId) so the modal can show it inline; anything
 * else has already been surfaced by the framework modal and rejects with Error('UNHANDLED').
 * retry=false: these writes email the client / create versions — never double-submit.
 */
export function commitAsync<R>(mutationClass: unknown, input: Record<string, unknown>): Promise<R> {
	return new Promise((resolve, reject) => {
		Util.CommitMutation(
			mutationClass,
			input,
			(response: R) => resolve(response),
			false,
			null,
			(transaction: unknown, onFailure: (t: unknown) => unknown) => {
				const id = getValidationErrorMessageId(String(transaction));
				if (id) return reject(new Error(id));
				onFailure(transaction);
				reject(new Error('UNHANDLED'));
			}
		);
	});
}

export const requestTicket = async (input: {projectId: string; fileName: string; sizeBytes: number}): Promise<UploadTicket> => {
	const response = await commitAsync<{requestClientAssetUpload: {ticket: UploadTicket | null} | null}>(
		RequestUploadMutation,
		input
	);
	const ticket = response.requestClientAssetUpload?.ticket;
	if (!ticket) throw new Error('UPLOAD_FAILED');
	return ticket;
};
